import React from 'react';
import { Link } from 'react-router-dom';
import { Field, reduxForm } from 'redux-form';
import { Alert, Form, Button, Row, Col, ControlLabel } from 'react-bootstrap';
import { Input, DateInput, TimeInput, Checkbox, FormActions, MultiSelect, HtmlEditor } from '../../common/components/formControls';
import CentersDropdownList from '../centers/DropdownList';
import validators from '../../common/services/validators';
import { withTranslation } from 'react-i18next';
import Localizations from '../localization';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import utils from '../../common/services/utils';

const min0 = validators.minValue(0);

const SettingsForm = ({ error, handleSubmit, pristine, submitting, submitSucceeded, t, initialValues, isReadonly, lang,
	recurrenceDaysOfWeek, publicPageUrl }) => {

	let id = initialValues ? initialValues.id : 0;

	const daysOfWeek = recurrenceDaysOfWeek ? Object.keys(recurrenceDaysOfWeek)
		.map(id => {
			const entry = recurrenceDaysOfWeek[id];
			return { value: entry.id, label: entry.displayName };
		}) : [];

	return (
		<Form horizontal onSubmit={handleSubmit}>
			{error && <Alert bsStyle='danger'>{error}</Alert>}
			{submitSucceeded && pristine && <Alert bsStyle='success'>{t('Common_UpdateSuccess')}</Alert>}
			<Field name='name' component={Input} disabled={isReadonly} required horizontal type='text' label={t('Workshop_Name')} />
			{id > 0 && <Localizations entityId={id} rootName={'workshops'} propertyName={'Name'} label={t('Workshop_Name')} resourceName={`Workshop.${id}.Name`} isHtml={false} isReadonly={isReadonly} />}
			<Field name='shortDescription' component={Input} disabled={isReadonly} horizontal type='text' label={t('Workshop_ShortDescription')} />
			{id > 0 && <Localizations entityId={id} rootName={'workshops'} propertyName={'ShortDescription'} label={t('Workshop_ShortDescription')} resourceName={`Workshop.${id}.ShortDescription`} isHtml={false} isReadonly={isReadonly} />}
			<Field name='description' component={HtmlEditor} disabled={isReadonly} lang={lang} horizontal type='text' label={t('Workshop_FullDescription')} />
			{id > 0 && <Localizations entityId={id} rootName={'workshops'} propertyName={'Description'} label={t('Workshop_FullDescription')} resourceName={`Workshop.${id}.Description`} isHtml={true} isReadonly={isReadonly} />}
			<CentersDropdownList render='field' name='centerId' required horizontal label={t('Workshop_Center')} validate={[validators.required]} />
			{id == 0 && <DateInput name='startDate' label={t('Workshop_StartDate')} isReadonly={isReadonly} validators={validators.required} required horizontal />}
			{id == 0 && <DateInput name='endDate' label={t('Workshop_EndDate')} isReadonly={isReadonly} validators={validators.required} required horizontal />}
			{id == 0 && <Field name='daysOfWeek' required horizontal component={MultiSelect} options={daysOfWeek} label={t('Workshop_DaysOfWeek')} validate={[validators.requiredArray]} />}
			{id == 0 && <Row>
				<Col componentClass={ControlLabel} sm={2}>
					{t('Workshop_Morning')}
				</Col>
				<Col sm={5}>
					<TimeInput name='morningStartTime' label={t('Workshop_StartTime')} isReadonly={isReadonly} horizontal />
				</Col>
				<Col sm={5}>
					<TimeInput name='morningEndTime' label={t('Workshop_FinishTime')} isReadonly={isReadonly} horizontal />
				</Col>
			</Row>}
			{id == 0 && <Row>
				<Col componentClass={ControlLabel} sm={2}>
					{t('Workshop_Afternoon')}
				</Col>
				<Col sm={5}>
					<TimeInput name='afternoonStartTime' label={t('Workshop_StartTime')} isReadonly={isReadonly} horizontal/>
				</Col>
				<Col sm={5}>
					<TimeInput name='afternoonEndTime' label={t('Workshop_FinishTime')} isReadonly={isReadonly} horizontal/>
				</Col>
			</Row>}
			<Field name='fullDayPrice' component={Input} disabled={isReadonly} horizontal type='text' label={t('Workshop_FullDayPrice')} validate={[validators.number, min0]} />
			<Field name='fullWeekPrice' component={Input} disabled={isReadonly} horizontal type='text'
				label={t('Workshop_FullWeekPrice')} validate={[validators.number, min0]} help={t('Workshop_FullWeekPrice_Help')}/>
			<Field name='halfDayPrice' component={Input} disabled={isReadonly} horizontal type='text' label={t('Workshop_HalfDayPrice')} validate={[validators.number, min0]} />
			<Field name='capacity' component={Input} disabled={isReadonly} horizontal type='text' label={t('Workshop_Capacity')} validate={[validators.integer, min0]} />
			<Row>
				<Col componentClass={ControlLabel} sm={2}>
					{t('Workshop_FromAge')}
				</Col>
				<Col sm={5}>
					<Field name='fromAgeYears' component={Input} disabled={isReadonly} horizontal required type='text' label={t('Workshop_Years')} validate={[validators.required, validators.number]} />
				</Col>
				<Col sm={5}>
					<Field name='fromAgeMonths' component={Input} disabled={isReadonly} horizontal required type='text' label={t('Workshop_Months')} validate={[validators.required, validators.number]} />
				</Col>
			</Row>
			<Row>
				<Col componentClass={ControlLabel} sm={2}>
					{t('Workshop_ToAge')}
				</Col>
				<Col sm={5}>
					<Field name='toAgeYears' component={Input} disabled={isReadonly} horizontal required type='text' label={t('Workshop_Years')} validate={[validators.required, validators.number]} />
				</Col>
				<Col sm={5}>
					<Field name='toAgeMonths' component={Input} disabled={isReadonly} horizontal required type='text' label={t('Workshop_Months')} validate={[validators.required, validators.number]} />
				</Col>
			</Row>

			<Field name='allowIndividualDays' component={Checkbox} disabled={isReadonly} horizontal label={t('Workshop_AllowIndividualDays')} normalize={(v) => { return !!v }} />
			<Field name='allowHalfDaySessions' component={Checkbox} disabled={isReadonly} horizontal label={t('Workshop_AllowHalfDaySessions')} normalize={(v) => { return !!v }} />
			<Field name='allowBooking' component={Checkbox} disabled={isReadonly} horizontal label={t('Workshop_AllowBooking')} normalize={(v) => { return !!v }} />

			<Row>
				<Col sm={5} smOffset={2}>
					<Field name='hidden' component={Checkbox} labelCol={0} disabled={isReadonly} horizontal label={t('Workshop_Hidden')} help={t('Workshop_Hidden_Hint')} normalize={(v) => { return !!v }} />
				</Col>
				<Col sm={5} style={{ marginBottom: 15 }}>
					<a href={publicPageUrl} target='_blank'>{t('Workshop_PublicPage')}</a>
					<a onClick={() => utils.copyToClipboard(publicPageUrl)} title='Copy' style={{ marginLeft: '10px', cursor: 'pointer' }}><FontAwesomeIcon icon={faCopy} /></a>
				</Col>
			</Row>

			<FormActions horizontal>
				<Link to='/workshops' className='btn btn-default'>{t('Common_BackToList')}</Link>
				<Button type='submit' disabled={pristine || submitting || isReadonly} bsStyle='primary'>{t('Common_Save')}</Button>
			</FormActions>
		</Form>
	);
};

export default reduxForm({ form: 'workshop', enableReinitialize: true })(withTranslation()(SettingsForm));